import { useGetClassesQuery, useAssignClassMutation, useUpdateLessonStatusMutation } from 'util/rtk-api'

import { DoneButton } from 'components/blue-button'
import DismissButton from 'components/dismiss-button'
import Dropdown from 'components/selector'
import { StudentTable } from 'components/tables/student-table'
import { ClassContextProvider } from 'contexts/class-context'
import styles from 'css/components/assign-class-overlay.module.css'
import listView from 'img/list-view.svg'
import { useState, useEffect } from 'react'
import { Class } from 'types'

import { DifficultyPills } from 'components/tables/difficulty-pills'
import { deriveGradeBand } from 'util/grade-band'

interface AssignClassOverlayProps {
    lessonId: string
    lessonGradeBand?: string
    onDismiss: () => void
    setIsClassAssigned: (isAssigned: boolean) => void
    setAssignedClass: (classData: { classId: string; name: string } | null) => void
}

export const AssignClassOverlay = ({ lessonId, lessonGradeBand, onDismiss, setIsClassAssigned, setAssignedClass }: AssignClassOverlayProps) => {
    const { data, isLoading, error } = useGetClassesQuery(undefined)
    const [assignClass, { isLoading: isAssigningClass }] = useAssignClassMutation()
    const [currentTableDifficulty, setCurrentTableDifficulty] = useState('All')
    const [selectedClass, setSelectedClass] = useState<Class | null>(null)
    const [students, setStudents] = useState([])
    const [updateLessonStatus, { isLoading: isUpdatingLessonStatus }] = useUpdateLessonStatusMutation()

    const classes = [...(data?.classes || [])].sort((a, b) => a.name.localeCompare(b.name))

    useEffect(() => {
        if (selectedClass) {
            setStudents(selectedClass.students)
        }
    }, [selectedClass])

    const handleClassSelect = (className: string) => {
        const selectedClass = classes.find(c => c.name === className)
        setSelectedClass(selectedClass)
    }

    const handleDone = () => {
        onDismiss()
        updateLessonStatus({ lessonId, status: 'Ready' })
        assignClass({ lessonId, classId: selectedClass.classId }).then(() => {
            setIsClassAssigned(true)
            setAssignedClass(selectedClass)
        })
    }

    // Check for grade band mismatch
    const selectedClassGradeBand = selectedClass?.grades ? deriveGradeBand(selectedClass.grades) : null
    const hasGradeBandMismatch = lessonGradeBand && selectedClassGradeBand && lessonGradeBand !== selectedClassGradeBand

    return (
        <div className="overlay">
            <div className="overlay-content-container">
                <div className="overlay-content-header">
                    Assign Class
                    <DismissButton onClick={onDismiss} />
                </div>
                <div className="overlay-content-body">
                    <div className={styles.classDropdownContainer}>
                        {isLoading ? (
                            <div>Loading classes...</div>
                        ) : error ? (
                            <div>Error loading classes</div>
                        ) : (
                            <Dropdown options={classes.map(c => c.name)} onSelect={handleClassSelect} style={styles.width40} placeholder="Select Your Class" value={selectedClass?.name} />
                        )}
                    </div>
                    {selectedClass ? (
                        <ClassContextProvider initialClass={selectedClass}>
                            <div className={styles.classInfoSection}>
                                <span>
                                    Class is {selectedClass.grades?.length === 1 ? 'grade' : 'grades'} {selectedClass.grades?.join(', ') || 'N/A'}.
                                    {' '}Questions will target grades {selectedClassGradeBand}.
                                </span>
                                {' '}<a className={styles.changeLink} onClick={() => setSelectedClass(null)}>change</a>
                                {hasGradeBandMismatch && (
                                    <div className={styles.gradeBandWarning}>
                                        Note: Questions were generated for grades {lessonGradeBand}. This class is grades {selectedClassGradeBand}.
                                    </div>
                                )}
                            </div>
                            <DifficultyPills currentDifficulty={currentTableDifficulty} handleDifficultyChange={setCurrentTableDifficulty} />
                            <div className={styles.studentTableContainer}>
                                <StudentTable
                                    keyFilter={new Set(['difficulty', 'studentName', 'grade'])}
                                    style={{}}
                                    currentDifficulty={currentTableDifficulty}
                                    renderHeader={true}
                                />
                            </div>
                            <div className={styles.alignRight}>
                                <DoneButton onClick={handleDone} />
                            </div>
                        </ClassContextProvider>
                    ) : (
                        <div className={styles.noClassSelectedContainer}>
                            <img src={listView} alt="List View" />
                            Class roster for your selection will show up here
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}